import React from "react";
import { Route, Switch } from "react-router-dom";

import { IconCaretDownSmall } from "~/components/icon";
import { SkeletonBox } from "~/components/skeleton/box";
import { cx } from "~/tools/class-variance";
import { idOrIdentifierSchema } from "~/tools/id-or-identifier";
import { useScoped } from "~/tools/impulse";

import { ApplicationDomainSwitchLink } from "../../components/application-domain-switch-link";
import { DropdownWithHost } from "../../components/dropdown-with-host";
import { SmartHistory, SmartHistoryBackButton, useSmartHistoryQueue } from "../../components/react/smart-history";
import { queryUIConfig } from "../../queries";
import { useQuery } from "../../queries/react-query";
import { Breadcrumbs } from "../../routes/breadcrumbs/Breadcrumbs";
import { DashboardSaveButton } from "../../routes/dashboards/dashboard/save-button";
import { COMPONENT_PATH, DASHBOARD_PATH, VIEW_PATH } from "../../routes/Routes";

import { SaveViewButton } from "./_save-view-button";
import { HamburgerMenu } from "./HamburgerMenu";
import { SnapshotIndication } from "./SnapshotIndication";

export const TopNav: React.FC = () => {
	const smartHistoryQueue = useSmartHistoryQueue();
	const isSmartHistoryShort = useScoped((scope) => smartHistoryQueue.getValue(scope).size() < 2);

	return (
		<div className="mr-2 flex items-center gap-6">
			<HamburgerMenu />

			{!isSmartHistoryShort && <SmartHistoryBackButton queue={smartHistoryQueue} />}

			<DropdownWithHost
				align={{ offset: [0, 3] }}
				className="min-w-fit"
				destroyPopupOnHide
				overlay={<SmartHistory queue={smartHistoryQueue} />}
				overlayClassName={cx(
					"w-160 max-w-full overflow-hidden rounded-xs border border-gray-300 border-solid bg-white px-1 pt-0 pb-2 shadow-lg",
				)}
				placement="bottomLeft"
				trigger={["click"]}
				triggerElement={
					<button className="top-nav-button top-nav-button-skinny rounded-l-none" type="button">
						<IconCaretDownSmall aria-label="caret-down" width="7" />
					</button>
				}
				type="dark"
				visible={!isSmartHistoryShort}
			>
				<Breadcrumbs isRightBorderFlat={!isSmartHistoryShort} />
			</DropdownWithHost>

			<Switch>
				<Route path={[COMPONENT_PATH, VIEW_PATH]}>
					<SaveViewButton />

					<SnapshotIndication />
				</Route>

				<Route
					path={DASHBOARD_PATH}
					render={({ match: { params } }) => (
						<DashboardSaveButton idOrIdentifier={idOrIdentifierSchema.parse(params.idOrIdentifier)} />
					)}
				/>
			</Switch>

			<TopNavModeSwitch />
		</div>
	);
};

const TopNavModeSwitch: React.FC = () => {
	const { data } = useQuery(queryUIConfig);
	const configuredApplicationDomains = data?.applicationDomains;

	return (
		<SkeletonBox
			className={cx(
				"h-7 w-25",
				configuredApplicationDomains != null && configuredApplicationDomains.length < 2 && "hidden",
			)}
			when={configuredApplicationDomains}
		>
			{(loadedConfiguredApplicationDomains) =>
				loadedConfiguredApplicationDomains.length > 1 && (
					<div className="flex size-full gap-px" role="radiogroup">
						{loadedConfiguredApplicationDomains.map((applicationDomain, i) => (
							<ApplicationDomainSwitchLink
								applicationDomain={applicationDomain}
								className="mode-switch-option-label:sr-only mode-switch-option-selected:top-nav-button-selected top-nav-button flex flex-1 mode-switch-option-selected:cursor-default items-center justify-center rounded-none mode-switch-option-icon:stroke-[1.2px] mode-switch-option-icon:text-lg first-of-type:rounded-l-xs last-of-type:rounded-r-xs"
								isDefault={i === 0}
								key={applicationDomain}
								role="radio"
							/>
						))}
					</div>
				)
			}
		</SkeletonBox>
	);
};
